import React from "react";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { format } from "date-fns";

const COLORS = {
  primary: "#3B0069",
  primarySoft: "#F4EFFF",
  accent: "#FF7C01",
  text: "#0F172A",
  muted: "#64748B",
  border: "#D7E0EA",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  success: "#15803D",
  successBg: "#DCFCE7",
  warning: "#B45309",
  warningBg: "#FEF3C7",
  danger: "#B91C1C",
  dangerBg: "#FEE2E2",
  neutralBg: "#E2E8F0",
  neutral: "#475569",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 92,
    paddingBottom: 54,
    paddingHorizontal: 28,
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    position: "absolute",
    top: 20,
    left: 28,
    right: 28,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 10,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  logoBox: {
    width: 44,
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.surfaceAlt,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    overflow: "hidden",
  },
  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  logoFallback: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.primary,
  },
  reportTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: COLORS.primary,
  },
  companyName: {
    fontSize: 10,
    color: COLORS.text,
    marginTop: 2,
  },
  headerMeta: {
    alignItems: "flex-end",
    maxWidth: 220,
  },
  headerMetaText: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 2,
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    left: 28,
    right: 28,
    bottom: 18,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 8,
  },
  footerText: {
    fontSize: 8,
    color: COLORS.muted,
  },
  pageHeading: {
    marginBottom: 14,
  },
  pageTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.text,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 10,
    color: COLORS.muted,
  },
  card: {
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  highlightCard: {
    backgroundColor: COLORS.surfaceAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    padding: 14,
    marginBottom: 14,
  },
  sectionTitleWrap: {
    backgroundColor: COLORS.primarySoft,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.primary,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.primary,
  },
  sectionNote: {
    fontSize: 8,
    color: COLORS.muted,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
    marginBottom: 8,
  },
  metaItem: {
    width: "50%",
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  metaLabel: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 10,
    color: COLORS.text,
    fontWeight: "bold",
  },
  tagGroup: {
    marginTop: 8,
  },
  tagTitle: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 5,
  },
  tagsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -3,
  },
  tag: {
    marginHorizontal: 3,
    marginBottom: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: COLORS.primarySoft,
  },
  tagText: {
    fontSize: 8,
    color: COLORS.primary,
  },
  kpiGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -5,
    marginBottom: 6,
  },
  kpiCard: {
    width: "25%",
    paddingHorizontal: 5,
    marginBottom: 10,
  },
  kpiCardInner: {
    backgroundColor: COLORS.surfaceAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 10,
    // minHeight: 78,
  },
  kpiLabel: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 14,
    fontWeight: "semibold",
    color: COLORS.primary,
    // marginBottom: 4,
  },
  kpiSubtext: {
    fontSize: 8,
    color: COLORS.muted,
  },
  statGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -4,
    marginBottom: 6,
  },
  statCard: {
    width: "33.33%",
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  statCardInner: {
    backgroundColor: COLORS.surfaceAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    padding: 10,
  },
  statKey: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 5,
  },
  statValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.text,
  },
  tableWrap: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: COLORS.primarySoft,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  tableHeaderCell: {
    paddingHorizontal: 6,
    paddingVertical: 7,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.primary,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  tableCell: {
    paddingHorizontal: 4,
    paddingVertical: 7,
    justifyContent: "center",
  },
  cellText: {
    fontSize: 8,
    color: COLORS.text,
    lineHeight: 1.35,
  },
  textRight: {
    textAlign: "right",
  },
  textCenter: {
    textAlign: "center",
  },
  emptyState: {
    padding: 10,
    fontSize: 8,
    color: COLORS.muted,
    textAlign: "center",
  },
  badge: {
    alignSelf: "flex-start",
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 50,
  },
  badgeText: {
    fontSize: 7,
    fontWeight: "bold",
  },
  amountRow: {
    marginBottom: 10,
  },
  amountRowHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  amountLabel: {
    fontSize: 9,
    color: COLORS.text,
    fontWeight: "bold",
    flex: 1,
    marginRight: 8,
  },
  amountValue: {
    fontSize: 9,
    color: COLORS.primary,
    fontWeight: "bold",
  },
  barTrack: {
    height: 6,
    backgroundColor: COLORS.neutralBg,
    borderRadius: 999,
    overflow: "hidden",
  },
  barFill: {
    height: 6,
    backgroundColor: COLORS.accent,
    borderRadius: 999,
  },
  placeholderHero: {
    backgroundColor: COLORS.primary,
    borderRadius: 18,
    padding: 18,
    marginBottom: 14,
  },
  placeholderEyebrow: {
    fontSize: 8,
    color: "#BFDBFE",
    marginBottom: 6,
  },
  placeholderHeroTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 8,
  },
  placeholderHeroText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#DBEAFE",
  },
  featureCardInner: {
    backgroundColor: COLORS.surfaceAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 10,
    padding: 12,
    minHeight: 90,
  },
  featureLabel: {
    fontSize: 8,
    color: COLORS.muted,
    marginBottom: 8,
  },
  featureValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.text,
    marginBottom: 6,
  },
  featureDescription: {
    fontSize: 8,
    color: COLORS.muted,
    lineHeight: 1.4,
  },
  listWrap: {
    marginTop: 6,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  listDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: COLORS.accent,
    marginTop: 5,
    marginRight: 8,
  },
  listText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.5,
    color: COLORS.text,
  },
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
};

// if Name like Ashok Industries -> AI, If SiteMaster -> SI
const getInitials = (name) => {
  return name
    ?.split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const toLabel = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "-";
  return `Rs. ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatDateValue = (value, pattern = "dd MMM yyyy") => {
  if (!value) return "-";
  try {
    return format(new Date(value), pattern);
  } catch (error) {
    return String(value);
  }
};

const formatDateTimeValue = (value) =>
  formatDateValue(value, "dd MMM yyyy, hh:mm a");

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const displayValue = (value) => {
  if (!hasValue(value) && value !== 0) return "-";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};

const getCellFlex = (flex = 1) => ({
  flexGrow: flex,
  flexBasis: 0,
});

const compactStyles = (...styleEntries) => styleEntries.filter(Boolean);

export const buildPlaceholderGlobalReportData = ({
  companyName,
  startDate,
  endDate,
  selectedSections = [],
  selectedProjects = [],
  generatedAt,
}) => {
  const safeSections = toArray(selectedSections).filter(Boolean);
  const safeProjects = toArray(selectedProjects).filter(Boolean);

  return {
    meta: {
      preview_state: "empty",
      report_name: "Global Project Report",
      company_name: companyName || "Organization",
      generated_at: generatedAt || new Date().toISOString(),
      period: {
        start: startDate,
        end: endDate,
      },
      selected_sections: safeSections.length
        ? safeSections
        : ["Choose sections from the left"],
      selected_projects: safeProjects.length
        ? safeProjects
        : ["Choose projects from the left"],
    },
    summary: {
      total_expenses: 122574,
      total_dues: 390370.29,
      total_received: 19505,
      balance: -103069,
    },
    org_finance: {
      analytics: {
        project_wise_expenses: [
          {
            project: safeProjects[0] || "Aparajita Buildings",
            amount: 48574,
          },
          {
            project: safeProjects[1] || "New SkyRise Apartments",
            amount: 40980,
          },
          {
            project: safeProjects[2] || "Site Mutation",
            amount: 33020,
          },
        ],
        category_wise_expenses: [
          {
            category: "Civil Works",
            amount: 48200,
          },
          {
            category: "Procurement",
            amount: 28974,
          },
          {
            category: "Vendor Payments",
            amount: 23750,
          },
          {
            category: "Petty Cash",
            amount: 21650,
          },
        ],
      },
    },
    preview_highlights: [
      {
        label: "Layout",
        value: "Left-side controls + right-side live PDF preview",
        description: "Keep the report visible while you refine filters.",
      },
      {
        label: "Date Range",
        value: `${formatDateValue(startDate)} - ${formatDateValue(endDate)}`,
        description: "Presets and custom dates both map into the PDF period.",
      },
      {
        label: "Sections",
        value: `${safeSections.length || 0} selected`,
        description: "Only the chosen report modules will render as pages.",
      },
      {
        label: "Projects",
        value: `${safeProjects.length || 0} selected`,
        description: "Compact project chips keep the selector focused and fast.",
      },
    ],
    preview_notes: [
      "Choose a date preset or set a custom start and end date for the reporting period.",
      "Select the sections and projects you want included in the final PDF.",
      "Click Generate Preview to replace this sample document with live organization data.",
    ],
  };
};

const StatusBadge = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  let backgroundColor = COLORS.neutralBg;
  let color = COLORS.neutral;

  if (
    ["approved", "completed", "active", "resolved", "ordered", "converted"].includes(
      normalized
    )
  ) {
    backgroundColor = COLORS.successBg;
    color = COLORS.success;
  } else if (
    ["pending", "open", "partial", "quotation_active", "not_started"].includes(
      normalized
    )
  ) {
    backgroundColor = COLORS.warningBg;
    color = COLORS.warning;
  } else if (
    ["rejected", "cancelled", "delayed", "halted"].includes(normalized)
  ) {
    backgroundColor = COLORS.dangerBg;
    color = COLORS.danger;
  }

  return (
    <View style={[styles.badge, { backgroundColor }]}>
      <Text style={[styles.badgeText, { color }]}>
        {displayValue(status).toUpperCase()}
      </Text>
    </View>
  );
};

const SectionTitle = ({ title, note }) => (
  <View style={styles.sectionTitleWrap}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {note ? <Text style={styles.sectionNote}>{note}</Text> : null}
  </View>
);

const TagGroup = ({ title, items }) => {
  const rows = toArray(items);

  if (!rows.length) return null;

  return (
    <View style={styles.tagGroup}>
      <Text style={styles.tagTitle}>{title}</Text>
      <View style={styles.tagsRow}>
        {rows.map((item, index) => (
          <View key={`${title}-${index}`} style={styles.tag}>
            <Text style={styles.tagText}>{displayValue(item)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const KpiGrid = ({ items }) => {
  const cards = toArray(items).filter((item) => item);

  if (!cards.length) return null;

  return (
    <View style={styles.kpiGrid}>
      {cards.map((item, index) => (
        <View key={`${item.label}-${index}`} style={styles.kpiCard} wrap={false}>
          <View style={styles.kpiCardInner}>
            <Text style={styles.kpiLabel}>{item.label}</Text>
            <Text style={styles.kpiValue}>{displayValue(item.value)}</Text>
            {item.subtext ? <Text style={styles.kpiSubtext}>{item.subtext}</Text> : null}
          </View>
        </View>
      ))}
    </View>
  );
};

const FeatureGrid = ({ title, note, items }) => {
  const cards = toArray(items).filter((item) => item);

  if (!cards.length) return null;

  return (
    <View style={styles.card}>
      <SectionTitle title={title} note={note} />
      <View style={styles.kpiGrid}>
        {cards.map((item, index) => (
          <View key={`${item.label}-${index}`} style={styles.kpiCard} wrap={false}>
            <View style={styles.featureCardInner}>
              <Text style={styles.featureLabel}>{item.label}</Text>
              <Text style={styles.featureValue}>{displayValue(item.value)}</Text>
              {item.description ? (
                <Text style={styles.featureDescription}>{item.description}</Text>
              ) : null}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const ObjectStatGrid = ({ title, data }) => {
  const entries = Object.entries(data || {});

  if (!entries.length) return null;

  return (
    <View style={styles.card}>
      <SectionTitle title={title} />
      <View style={styles.statGrid}>
        {entries.map(([key, value]) => (
          <View key={key} style={styles.statCard} wrap={false}>
            <View style={styles.statCardInner}>
              <Text style={styles.statKey}>{toLabel(key)}</Text>
              <Text style={styles.statValue}>{displayValue(value)}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const AmountBarList = ({ title, items, labelKey, valueKey }) => {
  const rows = toArray(items);

  return (
    <View style={styles.card}>
      <SectionTitle title={title} />
      {rows.length === 0 ? (
        <Text style={styles.emptyState}>No data available.</Text>
      ) : (
        (() => {
          const maxValue = Math.max(
            ...rows.map((row) => Number(row?.[valueKey] || 0)),
            0
          );

          return rows.map((row, index) => {
            const amount = Number(row?.[valueKey] || 0);
            const width =
              maxValue > 0 ? `${Math.max((amount / maxValue) * 100, 4)}%` : "0%";

            return (
              <View key={`${row?.[labelKey] || index}`} style={styles.amountRow}>
                <View style={styles.amountRowHeader}>
                  <Text style={styles.amountLabel}>
                    {displayValue(row?.[labelKey])}
                  </Text>
                  <Text style={styles.amountValue}>{formatCurrency(amount)}</Text>
                </View>
                <View style={styles.barTrack}>
                  <View style={[styles.barFill, { width }]} />
                </View>
              </View>
            );
          });
        })()
      )}
    </View>
  );
};

const DataTable = ({ title, columns, rows, emptyText = "No data available." }) => {
  const safeRows = toArray(rows);

  return (
    <View style={styles.card}>
      {title ? <SectionTitle title={title} /> : null}
      <View style={styles.tableWrap}>
        <View style={styles.tableHeader}>
          {columns.map((column) => (
            <View
              key={column.key}
              style={[styles.tableHeaderCell, getCellFlex(column.flex)]}
            >
              <Text
                style={compactStyles(
                  styles.tableHeaderText,
                  column.align === "right" && styles.textRight,
                  column.align === "center" && styles.textCenter
                )}
              >
                {column.label}
              </Text>
            </View>
          ))}
        </View>

        {safeRows.length === 0 ? (
          <Text style={styles.emptyState}>{emptyText}</Text>
        ) : (
          safeRows.map((row, rowIndex) => (
            <View
              key={`row-${rowIndex}`}
              style={compactStyles(
                styles.tableRow,
                rowIndex === safeRows.length - 1 && styles.tableRowLast
              )}
              wrap={false}
            >
              {columns.map((column) => {
                const rawValue = row?.[column.key];

                return (
                  <View
                    key={`${column.key}-${rowIndex}`}
                    style={[styles.tableCell, getCellFlex(column.flex)]}
                  >
                    {column.type === "status" ? (
                      <StatusBadge status={rawValue} />
                    ) : column.render ? (
                      column.render(rawValue, row)
                    ) : (
                      <Text
                        style={compactStyles(
                          styles.cellText,
                          column.align === "right" && styles.textRight,
                          column.align === "center" && styles.textCenter
                        )}
                      >
                        {displayValue(rawValue)}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          ))
        )}
      </View>
    </View>
  );
};

const PageShell = ({ meta, logoSrc, title, subtitle, children }) => (
  <Page size="A4" style={styles.page} wrap>
    <View style={styles.header} fixed>
      <View style={styles.headerLeft}>
        <View style={styles.logoBox}>
          {logoSrc ? (
            <Image src={logoSrc} style={styles.logoImage} />
          ) : (
            <Text style={styles.logoFallback}>
              {getInitials(meta?.company_name)}
            </Text>
          )}
        </View>
        <View>
          <Text style={styles.reportTitle}>
            {meta?.report_name || "Global Report"}
          </Text>
          <Text style={styles.companyName}>
            {meta?.company_name || "Organization"}
          </Text>
        </View>
      </View>

      <View style={styles.headerMeta}>
        <Text style={styles.headerMetaText}>
          Generated: {formatDateTimeValue(meta?.generated_at)}
        </Text>
        <Text style={styles.headerMetaText}>
          Period: {formatDateValue(meta?.period?.start)} -{" "}
          {formatDateValue(meta?.period?.end)}
        </Text>
      </View>
    </View>

    <View style={styles.pageHeading}>
      <Text style={styles.pageTitle}>{title}</Text>
      {subtitle ? <Text style={styles.pageSubtitle}>{subtitle}</Text> : null}
    </View>

    {children}

    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>Generated by SiteMaster</Text>
      <Text
        style={styles.footerText}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  </Page>
);

const GlobalPDFReport = ({ data, logoSrc, isPlaceholder = false }) => {
  if (!data) return null;

  const meta = data.meta || {};
  const summary = data.summary || data.org_finance?.summary || {};
  const selectedSections = new Set(toArray(meta.selected_sections).map(String));
  const hasSection = (sectionId) =>
    selectedSections.size === 0 || selectedSections.has(sectionId);

  const financeSummaryCards = [
    { label: "TOTAL EXPENSES", value: formatCurrency(summary.total_expenses) },
    { label: "DUES", value: formatCurrency(summary.total_dues) },
    { label: "RECEIVED", value: formatCurrency(summary.total_received) },
    { label: "BALANCE", value: formatCurrency(summary.balance) },
  ];

  if (isPlaceholder) {
    return (
      <Document title={meta?.report_name || "Global Project Report"}>
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Global Report Preview"
          subtitle="A polished sample layout is shown here until you generate live report data."
        >
          <View style={styles.placeholderHero}>
            <Text style={styles.placeholderEyebrow}>
              PREVIEWING THE FINAL PDF EXPERIENCE
            </Text>
            <Text style={styles.placeholderHeroTitle}>
              Build a modern multi-project report with confidence
            </Text>
            <Text style={styles.placeholderHeroText}>
              This preview reflects your current filters and demonstrates how the
              final PDF will look once data is generated from the API.
            </Text>
          </View>

          <View style={styles.highlightCard}>
            <View style={styles.metaRow}>
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Organization</Text>
                <Text style={styles.metaValue}>
                  {displayValue(meta.company_name || "Organization")}
                </Text>
              </View>
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Preview Mode</Text>
                <Text style={styles.metaValue}>Sample PDF Layout</Text>
              </View>
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Period Start</Text>
                <Text style={styles.metaValue}>
                  {formatDateValue(meta?.period?.start)}
                </Text>
              </View>
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Period End</Text>
                <Text style={styles.metaValue}>
                  {formatDateValue(meta?.period?.end)}
                </Text>
              </View>
            </View>

            <TagGroup title="Selected Sections" items={meta.selected_sections} />
            <TagGroup title="Selected Projects" items={meta.selected_projects} />
          </View>

          <KpiGrid items={financeSummaryCards} />

          <FeatureGrid
            title="Preview Highlights"
            note="The live API response will replace this sample without leaving the page."
            items={data.preview_highlights}
          />

          <View style={styles.card}>
            <SectionTitle
              title="How To Use This Workspace"
              note="A smoother reporting flow for everyday operations."
            />
            <View style={styles.listWrap}>
              {toArray(data.preview_notes).map((note, index) => (
                <View key={`preview-note-${index}`} style={styles.listItem}>
                  <View style={styles.listDot} />
                  <Text style={styles.listText}>{displayValue(note)}</Text>
                </View>
              ))}
            </View>
          </View>
        </PageShell>

        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Sample Finance Layout"
          subtitle="Representative styling for the live PDF that appears after generation"
        >
          <AmountBarList
            title="Project-wise Expenses"
            items={data.org_finance?.analytics?.project_wise_expenses}
            labelKey="project"
            valueKey="amount"
          />

          <DataTable
            title="Category-wise Expenses"
            rows={data.org_finance?.analytics?.category_wise_expenses}
            columns={[
              { key: "category", label: "Category", flex: 2 },
              {
                key: "amount",
                label: "Amount",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
            ]}
          />
        </PageShell>
      </Document>
    );
  }

  const leadsStats = data.leads?.stats || {};
  const designStats = data.design?.stats || {};
  const procurementStats = data.procurement?.stats || {};
  const pettyCashSummary = data.petty_cash?.summary || {};

  return (
    <Document title={meta?.report_name || "Global Project Report"}>
      {hasSection("finance") && (hasValue(summary) || hasValue(data.org_finance)) && (
        <>
          <PageShell
            meta={meta}
            logoSrc={logoSrc}
            title="Organization Finance Summary"
            subtitle="Executive financial overview across selected projects"
          >
            <View style={styles.highlightCard}>
              <View style={styles.metaRow}>
                <View style={styles.metaItem}>
                  <Text style={styles.metaLabel}>Report Name</Text>
                  <Text style={styles.metaValue}>
                    {displayValue(meta.report_name || "Global Project Report")}
                  </Text>
                </View>
                <View style={styles.metaItem}>
                  <Text style={styles.metaLabel}>Generated At</Text>
                  <Text style={styles.metaValue}>
                    {formatDateTimeValue(meta.generated_at)}
                  </Text>
                </View>
                <View style={styles.metaItem}>
                  <Text style={styles.metaLabel}>Period Start</Text>
                  <Text style={styles.metaValue}>
                    {formatDateValue(meta?.period?.start)}
                  </Text>
                </View>
                <View style={styles.metaItem}>
                  <Text style={styles.metaLabel}>Period End</Text>
                  <Text style={styles.metaValue}>
                    {formatDateValue(meta?.period?.end)}
                  </Text>
                </View>
              </View>

              <TagGroup
                title="Selected Sections"
                items={toArray(meta.selected_sections).map(toLabel)}
              />
              <TagGroup title="Selected Projects" items={meta.selected_projects} />
            </View>

            <KpiGrid items={financeSummaryCards} />
          </PageShell>

          <PageShell
            meta={meta}
            logoSrc={logoSrc}
            title="Financial Analytics"
            subtitle="Project-wise and category-wise expense distribution"
          >
            <AmountBarList
              title="Project-wise Expenses"
              items={data.org_finance?.analytics?.project_wise_expenses}
              labelKey="project"
              valueKey="amount"
            />

            <DataTable
              title="Category-wise Expenses"
              rows={data.org_finance?.analytics?.category_wise_expenses}
              columns={[
                { key: "category", label: "Category", flex: 2 },
                {
                  key: "amount",
                  label: "Amount",
                  flex: 1,
                  align: "right",
                  render: (value) => (
                    <Text style={[styles.cellText, styles.textRight]}>
                      {formatCurrency(value)}
                    </Text>
                  ),
                },
              ]}
            />
          </PageShell>
        </>
      )}

      {hasSection("leads") && hasValue(data.leads) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Leads"
          subtitle="Pipeline health and latest lead activities"
        >
          <KpiGrid
            items={[
              { label: "TOTAL", value: displayValue(leadsStats.total || 0) },
              { label: "ACTIVE", value: displayValue(leadsStats.active || 0) },
              {
                label: "SCOPE CREATED",
                value: displayValue(leadsStats.scope_created || 0),
              },
              {
                label: "QUOTATION CREATED",
                value: displayValue(leadsStats.quotation_created || 0),
              },
              { label: "CONVERTED", value: displayValue(leadsStats.converted || 0) },
            ]}
          />

          <DataTable
            title="Lead Activities"
            rows={data.leads?.activities}
            columns={[
              { key: "date", label: "Date", flex: 1.1 },
              { key: "activity", label: "Activity", flex: 2.3 },
              { key: "lead_name", label: "Lead Name", flex: 1.8 },
              { key: "assigned_to", label: "Assigned To", flex: 1.5 },
              { key: "status", label: "Status", flex: 1, type: "status" },
            ]}
          />
        </PageShell>
      )}

      {hasSection("design") && hasValue(data.design) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Design"
          subtitle="Design throughput and pin resolution summary"
        >
          <KpiGrid
            items={[
              { label: "CREATED", value: displayValue(designStats.created || 0) },
              { label: "APPROVED", value: displayValue(designStats.approved || 0) },
              {
                label: "TASKS LINKED",
                value: displayValue(designStats.tasks_linked || 0),
              },
            ]}
          />

          <DataTable
            title="Pending Pins"
            rows={data.design?.pending_pins}
            columns={[
              { key: "design_name", label: "Design Name", flex: 1.8 },
              { key: "version", label: "Version", flex: 0.7, align: "center" },
              { key: "comment", label: "Comment", flex: 2.1 },
              { key: "created_by", label: "Created By", flex: 1.3 },
              {
                key: "datetime",
                label: "Datetime",
                flex: 1.6,
                render: (value) => (
                  <Text style={styles.cellText}>{formatDateTimeValue(value)}</Text>
                ),
              },
            ]}
          />

          <DataTable
            title="Resolved Pins"
            rows={data.design?.resolved_pins}
            columns={[
              { key: "design_name", label: "Design Name", flex: 1.7 },
              { key: "version", label: "Version", flex: 0.7, align: "center" },
              { key: "comment", label: "Comment", flex: 1.9 },
              { key: "created_by", label: "Created By", flex: 1.2 },
              {
                key: "datetime",
                label: "Datetime",
                flex: 1.5,
                render: (value) => (
                  <Text style={styles.cellText}>{formatDateTimeValue(value)}</Text>
                ),
              },
              { key: "resolved_by", label: "Resolved By", flex: 1.1 },
            ]}
          />
        </PageShell>
      )}

      {hasSection("procurement") && hasValue(data.procurement) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Procurement"
          subtitle="Procurement volume, status mix, and recent purchase orders"
        >
          <KpiGrid
            items={[
              {
                label: "MRS CREATED",
                value: displayValue(procurementStats.mrs_created || 0),
              },
              {
                label: "RFQ CREATED",
                value: displayValue(procurementStats.rfq_created || 0),
              },
              {
                label: "PO CREATED",
                value: displayValue(procurementStats.po_created || 0),
              },
            ]}
          />

          <ObjectStatGrid title="MR Status" data={data.procurement?.mr_status} />
          <ObjectStatGrid title="RFQ Status" data={data.procurement?.rfq_status} />
          <ObjectStatGrid title="PO Status" data={data.procurement?.po_status} />

          <DataTable
            title="Recent Purchase Orders"
            rows={data.procurement?.recent_pos}
            columns={[
              { key: "po_number", label: "PO Number", flex: 1.2 },
              { key: "vendor", label: "Vendor", flex: 1.6 },
              { key: "material", label: "Material", flex: 2.1 },
              {
                key: "amount",
                label: "Amount",
                flex: 1.1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              { key: "status", label: "Status", flex: 0.9, type: "status" },
              { key: "date", label: "Date", flex: 1 },
            ]}
          />
        </PageShell>
      )}

      {hasSection("vendors") && hasValue(data.vendors) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Vendors"
          subtitle="Vendor commitments, payments, and advances"
        >
          <DataTable
            title="Vendor Summary"
            rows={data.vendors?.summary}
            columns={[
              { key: "name", label: "Vendor", flex: 1.8 },
              {
                key: "advance_payment",
                label: "Advance",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "paid",
                label: "Paid",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "due",
                label: "Due",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "total_po_value",
                label: "Total PO Value",
                flex: 1.2,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
            ]}
          />

          <DataTable
            title="Advance Payments"
            rows={data.vendors?.advance_payments}
            columns={[
              { key: "vendor", label: "Vendor", flex: 1.8 },
              {
                key: "amount",
                label: "Amount",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              { key: "date", label: "Date", flex: 1 },
              { key: "method", label: "Method", flex: 1.2 },
            ]}
          />
        </PageShell>
      )}

      {hasSection("petty_cash") && hasValue(data.petty_cash) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Petty Cash"
          subtitle="Allocation, consumption, and approval visibility"
        >
          <KpiGrid
            items={[
              { label: "TOTAL", value: formatCurrency(pettyCashSummary.total) },
              {
                label: "ALLOCATED",
                value: formatCurrency(pettyCashSummary.allocated),
              },
              { label: "SPENT", value: formatCurrency(pettyCashSummary.spent) },
              { label: "BALANCE", value: formatCurrency(pettyCashSummary.balance) },
            ]}
          />

          <DataTable
            title="Team Allocation"
            rows={data.petty_cash?.team_allocation}
            columns={[
              { key: "member", label: "Member", flex: 1.8 },
              {
                key: "balance_left",
                label: "Balance Left",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "spendable",
                label: "Spendable",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "spent",
                label: "Spent",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              {
                key: "total_allocated",
                label: "Total Allocated",
                flex: 1.2,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
            ]}
          />

          <DataTable
            title="Approvals"
            rows={data.petty_cash?.approvals}
            columns={[
              { key: "category", label: "Category", flex: 1.4 },
              {
                key: "amount",
                label: "Amount",
                flex: 0.9,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              { key: "raised_by", label: "Raised By", flex: 1.2 },
              { key: "approved_by", label: "Approved By", flex: 1.3 },
              { key: "method", label: "Method", flex: 1 },
              { key: "date", label: "Date", flex: 0.9 },
              { key: "status", label: "Status", flex: 1, type: "status" },
            ]}
          />
        </PageShell>
      )}

      {hasSection("contractor_overview") && hasValue(data.contractor_overview) && (
        <PageShell
          meta={meta}
          logoSrc={logoSrc}
          title="Contractor Overview"
          subtitle="Workorder coverage, attendance, and payment visibility"
        >
          <DataTable
            title="Workorders"
            rows={data.contractor_overview?.workorders}
            columns={[
              { key: "id", label: "Workorder", flex: 1.1 },
              { key: "contractor", label: "Contractor", flex: 1.6 },
              { key: "project", label: "Project", flex: 1.5 },
              { key: "scope", label: "Scope", flex: 2.4 },
              { key: "status", label: "Status", flex: 1, type: "status" },
            ]}
          />

          <DataTable
            title="Attendance"
            rows={data.contractor_overview?.attendance}
            columns={[
              { key: "date", label: "Date", flex: 1 },
              { key: "present_count", label: "Present", flex: 0.9, align: "center" },
              { key: "contractor", label: "Contractor", flex: 1.4 },
              { key: "workorder", label: "Workorder", flex: 1.1 },
              { key: "hours", label: "Hours", flex: 0.8, align: "center" },
            ]}
          />

          <DataTable
            title="Payments"
            rows={data.contractor_overview?.payments}
            columns={[
              { key: "workorder", label: "Workorder", flex: 1.1 },
              { key: "contractor", label: "Contractor", flex: 1.4 },
              {
                key: "amount",
                label: "Amount",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
              { key: "payment_date", label: "Payment Date", flex: 1.1 },
              { key: "status", label: "Status", flex: 1, type: "status" },
            ]}
          />
        </PageShell>
      )}

      {toArray(data.projects).map((project, index) => (
        <PageShell
          key={`${project?.name || "project"}-${index}`}
          meta={meta}
          logoSrc={logoSrc}
          title={displayValue(project?.name || `Project ${index + 1}`)}
          subtitle="Project snapshot with tasks, inventory, workforce, and finance"
        >
          <KpiGrid
            items={[
              { label: "TASKS ONGOING", value: displayValue(project?.tasks?.ongoing || 0) },
              { label: "TASKS DELAYED", value: displayValue(project?.tasks?.delayed || 0) },
              {
                label: "NOT STARTED",
                value: displayValue(project?.tasks?.not_started || 0),
              },
              { label: "COMPLETED", value: displayValue(project?.tasks?.completed || 0) },
              { label: "HALTED", value: displayValue(project?.tasks?.halted || 0) },
            ]}
          />

          <KpiGrid
            items={[
              {
                label: "TOTAL MATERIALS",
                value: displayValue(project?.inventory?.total_materials || 0),
              },
              {
                label: "LOW STOCK",
                value: displayValue(project?.inventory?.low_stock || 0),
              },
              {
                label: "MRS RAISED",
                value: displayValue(project?.inventory?.mrs_raised || 0),
              },
            ]}
          />

          <DataTable
            title="Inventory Updates"
            rows={project?.inventory?.updates}
            columns={[
              { key: "material", label: "Material", flex: 1.4 },
              { key: "spec", label: "Spec", flex: 1.2 },
              { key: "quantity_change", label: "Quantity Change", flex: 1.1 },
              { key: "type", label: "Type", flex: 0.9 },
              { key: "updated_by", label: "Updated By", flex: 1.2 },
              { key: "date", label: "Date", flex: 0.9 },
            ]}
          />

          <KpiGrid
            items={[
              {
                label: "TOTAL WORKERS",
                value: displayValue(project?.workers?.total || 0),
              },
              {
                label: "PRESENT RATE",
                value: formatPercent(project?.workers?.present_rate),
              },
            ]}
          />

          <ObjectStatGrid title="Worker Type Breakdown" data={project?.workers?.types} />

          <KpiGrid
            items={[
              {
                label: "TOTAL EXPENSE",
                value: formatCurrency(project?.finance?.total_expense),
              },
              {
                label: "RECEIVED",
                value: formatCurrency(project?.finance?.received),
              },
              { label: "REFUNDS", value: formatCurrency(project?.finance?.refunds) },
              { label: "DUES", value: formatCurrency(project?.finance?.dues) },
            ]}
          />

          <DataTable
            title="Finance Transactions"
            rows={project?.finance?.transactions}
            columns={[
              { key: "type", label: "Type", flex: 1.2 },
              { key: "category", label: "Category", flex: 1.3 },
              { key: "txn_id", label: "Txn ID", flex: 1.2 },
              { key: "created_by", label: "Created By", flex: 1.3 },
              {
                key: "datetime",
                label: "Datetime",
                flex: 1.6,
                render: (value) => (
                  <Text style={styles.cellText}>{formatDateTimeValue(value)}</Text>
                ),
              },
              {
                key: "amount",
                label: "Amount",
                flex: 1,
                align: "right",
                render: (value) => (
                  <Text style={[styles.cellText, styles.textRight]}>
                    {formatCurrency(value)}
                  </Text>
                ),
              },
            ]}
          />
        </PageShell>
      ))}
    </Document>
  );
};

export default GlobalPDFReport;
